import API_CONFIG from '../config/api';

export interface SamplingSchedule {
  _id: string;
  date: string; // Format: YYYY-MM-DD
  startTime: string; // Format: HH:mm
  endTime: string; // Format: HH:mm
  capacity: number;
  isVacation: boolean;
  type: 'lab' | 'home';
  createdAt: string;
  updatedAt: string;
}

export interface CreateScheduleData {
  date: string;
  startTime: string;
  endTime: string;
  capacity: number;
  isVacation: boolean;
  type: 'lab' | 'home';
}

export interface UpdateScheduleData {
  date?: string;
  startTime?: string;
  endTime?: string;
  capacity?: number;
  isVacation?: boolean;
  type?: 'lab' | 'home';
}

// Get all schedules with optional filters
export const getSchedules = async (filters?: {
  type?: 'lab' | 'home';
  startDate?: string;
  endDate?: string;
}): Promise<SamplingSchedule[]> => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('لطفا ابتدا وارد حساب کاربری خود شوید.');
    }

    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);

    const response = await fetch(
      API_CONFIG.getUrl(`sampling-schedules${params.toString() ? `?${params.toString()}` : ''}`),
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'خطا در دریافت برنامه‌ها');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {

    throw error;
  }
};

// Get schedules for a specific month
export const getMonthSchedules = async (
  year: number,
  month: number,
  type?: 'lab' | 'home'
): Promise<SamplingSchedule[]> => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('لطفا ابتدا وارد حساب کاربری خود شوید.');
    }

    const params = new URLSearchParams({
      year: year.toString(),
      month: month.toString(),
    });
    if (type) params.append('type', type);

    const response = await fetch(
      API_CONFIG.getUrl(`sampling-schedules/month?${params.toString()}`),
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'خطا در دریافت برنامه‌های ماه');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {

    throw error;
  }
};

// Get schedule by date and type
export const getScheduleByDateAndType = async (
  date: string,
  type: 'lab' | 'home'
): Promise<SamplingSchedule | null> => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('لطفا ابتدا وارد حساب کاربری خود شوید.');
    }

    const response = await fetch(
      API_CONFIG.getUrl(`sampling-schedules/${date}/${type}`),
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'خطا در دریافت برنامه');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {

    throw error;
  }
};

// Create new schedule
export const createSchedule = async (scheduleData: CreateScheduleData): Promise<SamplingSchedule> => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('لطفا ابتدا وارد حساب کاربری خود شوید.');
    }

    const response = await fetch(
      API_CONFIG.getUrl('sampling-schedules'),
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(scheduleData),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'خطا در ایجاد برنامه');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {

    throw error;
  }
};

// Update schedule
export const updateSchedule = async (
  id: string,
  updates: UpdateScheduleData
): Promise<SamplingSchedule> => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('لطفا ابتدا وارد حساب کاربری خود شوید.');
    }

    const response = await fetch(
      API_CONFIG.getUrl(`sampling-schedules/${id}`),
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'خطا در بروزرسانی برنامه');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {

    throw error;
  }
};

// Delete schedule
export const deleteSchedule = async (id: string): Promise<void> => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('لطفا ابتدا وارد حساب کاربری خود شوید.');
    }

    const response = await fetch(
      API_CONFIG.getUrl(`sampling-schedules/${id}`),
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'خطا در حذف برنامه');
    }
  } catch (error) {

    throw error;
  }
};

// Helper function to convert Persian date to Gregorian
export const persianToGregorian = (persianDate: string): string => {
  // This is a simple placeholder - you may want to use a proper library like moment-jalaali
  // For now, returning the input as-is
  // In production, use a proper Persian to Gregorian date converter
  return persianDate;
};

// Helper function to convert Gregorian date to Persian
export const gregorianToPersian = (gregorianDate: string): string => {
  // This is a simple placeholder - you may want to use a proper library like moment-jalaali
  // For now, returning the input as-is
  // In production, use a proper Gregorian to Persian date converter
  return gregorianDate;
}; 